import React, {useEffect, useRef, useState} from 'react';
import ReactTable from 'react-table'
import Axios from 'axios'
import SearchBar from 'components/common/SearchBar'
import { Colors, Sizes, ButtonGroup, Button} from 'react-foundation'
import TableContainer from 'components/common/TableContainer'
import { API } from 'api/APIClient'
import {CopyToClipboard} from 'react-copy-to-clipboard';
import Modal from "react-responsive-modal";
import StandardModal from "../../common/StandardModal";
import {Toast} from "../../../utils/ToastUtil";
import ReactSelect from "../../common/ReactSelect";
import {PermissionUtil} from "../../../utils/PermissionUtil";
import {ConversationEditor} from "./ConversationEditor";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {NotificationManager} from "react-notifications";
import DatatableHelper from "../../../utils/DatatableHelper";

export const ConversationTable = (props) => {

    const {onSelected} = props;
    const {showCopyButton} = props;
    const {showSelectButton} = props;
    const {showEditButton} = props;
    const {showDeleteButton} = props;

    const [items, setItems] = useState([]);
    const [searchKey, setSearchKey] = useState("");
    const [searchOrganisationID, setSearchOrganisationID] = useState(undefined);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sorted, setSorted] = useState([{id: "id", desc: true}])
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(-1)
    const [organisationOptions, setOrganisationOptions] = useState([]);

    const [useTimeLimit, setUseTimeLimit] = useState(false);
    const [timeLimit, setTimeLimit] = useState(0);
    const [useSameSession, setUseSameSession] = useState(false)

    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);

    const searchTimer = useRef();

    useEffect(() => {
        fetchItems();

        if (PermissionUtil.allOrgs(PermissionUtil.PermissionCategories.conversationTypes)) {
            fetchOrganisations();
        }
    }, []);

    useEffect(() => {
        fetchItems();
    }, [page, pageSize, sorted, searchKey, searchOrganisationID]);

    function searchTextDidChange(newValue) {
        clearTimeout(searchTimer);
        searchTimer.current = setTimeout(() => {
            setSearchKey(newValue);
        }, 1000)
    }

    function organisationDidChange(newValue) {
        setSearchOrganisationID(newValue);
    }

    function handleSelected(item) {
        if (onSelected instanceof Function) {
            onSelected(item);
        }
    }

    function handleAddNewItem() {
        openModal(
            <ConversationEditor
                callback={modalDidCallback}
            />
        );
    }

    function handleEditItem(item) {
        openModal(
            <ConversationEditor
                conversation={item}
                callback={modalDidCallback}
            />
        );
    }

    function promptDeleteItem(item) {
        openModal(
            <StandardModal
                title="Delete Conversation"
                content={(
                    <div>Are you sure to delete the &quot;<strong>{item.name}</strong>&quot; Conversation? This action cannot be reverted.</div>
                )}
                buttons={(
                    <React.Fragment>
                        <button className="button secondary" type="button" onClick={closeModal}>
                            Cancel
                        </button>
                        <button className="button alert" type="button" onClick={() => handleDeleteItem(item)}>
                            Delete
                        </button>
                    </React.Fragment>
                )}
                isUploading={modalLoading}
            />
        )
    }

    function handleDeleteItem(item) {
        if (modalLoading) return;
        setModalLoading(true);

        const data = {
            id : item.id
        };

        Axios.post(API.conversation.deleteConversationType, data)
            .then((r) => {
                const resp = API.parse(r);
                if (resp.success) {
                    NotificationManager.success("Deleted item: " + item.name);
                } else {
                    NotificationManager.error(API.formatError(resp));
                }

                closeModal();
            })
            .catch((e) => {
                Toast.show(
                    "Could not delete item. An unknown error has occurred.",
                    Toast.DURATION_LONG,
                    Toast.TYPE_ERROR
                );

                closeModal();
            });
    }

    function openModal(content) {
        setModalOpen(true);
        setModalContent(content);
    }

    function closeModal() {
        setModalOpen(false);
        setModalContent([]);
        setModalLoading(false);

        fetchItems();
    }

    function modalDidCallback(action, data) {
        if (action === "submit") {
            closeModal();
        }
    }

    function fetchItems() {
        setIsLoading(true);

        let organisations = undefined;
        if (searchOrganisationID) {
            organisations = [searchOrganisationID];
        }

        const data = {
            page : page,
            pageSize : pageSize,
            sorted : sorted,
            filters : {
                search: searchKey,
                organisations
            }
        };

        Axios.post(API.conversation.getConversationTypesDT, data)
            .then((res) => {
                if (res.data.success) {
                    setItems(Object.values(res.data.data.rows));
                    setTotalPages(DatatableHelper.getTotalPages(res.data.data))
                } else if (res.data.error) {
                    console.log(API.formatError(res));
                }
                setIsLoading(false);
            })
            .catch((e) => {
                console.log(e);
                setIsLoading(false);
            });
    }

    function fetchOrganisations() {
        Axios.get(API.organisation.getOrganisations, {
            props : {
                appUsersOnly : 1
            }
        })
            .then((r) => {
                const resp = API.parse(r);
                if (resp.success) {
                    const organisations = resp.data.organisations;
                    if (organisations) {
                        const orgOptions = organisations.map((org) => {
                            return {
                                value : org.id.toString(),
                                label : org.name
                            }
                        });

                        orgOptions.unshift({
                            value : null,
                            label : "All Organisations"
                        });

                        setOrganisationOptions(orgOptions)
                    }
                } else {
                    console.log(API.formatError(resp));
                }
            })
            .catch((e) => {
                console.log(e);
            });
    }

    // RENDER

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
        },
        {
            Header: 'Key',
            accessor: 'key',
            sortable: false
        },
        {
            Header: 'Organisation',
            accessor: 'organisationName',
        },
        {
            Header: 'Actions',
            Cell: row => (
                <ButtonGroup size={Sizes.TINY}>

                    {showCopyButton === true &&
                        <CopyToClipboard
                            text={"{{ST.conversation.[" + row.original.key + "]" + (useTimeLimit ? "+TL"+timeLimit : "") + (useSameSession ? "+SS" : "") + " " + row.original.name + "}}"}
                            onCopy={() => handleSelected(row.original)}
                        >
                            <Button color={Colors.PRIMARY}>Copy to clipboard</Button>
                        </CopyToClipboard>
                    }

                    {showSelectButton === true &&
                        <React.Fragment>
                            <Button color={Colors.PRIMARY} onClick={() => handleSelected(row.original)}>Select</Button>
                        </React.Fragment>
                    }

                    {showEditButton === true &&
                        <Button
                            color={Colors.PRIMARY}
                            onClick={() => handleEditItem(row.original)}
                        >
                            <FontAwesomeIcon icon="edit"/>
                        </Button>
                    }

                    {showDeleteButton === true &&
                        <Button
                            color={Colors.ALERT}
                            onClick={() => promptDeleteItem(row.original)}
                        >
                            <FontAwesomeIcon icon="trash"/>
                        </Button>
                    }
                </ButtonGroup>
            ),
            show: showCopyButton || showSelectButton || showEditButton || showDeleteButton,
            sortable: false
        }
    ]

    const filters = (
        <React.Fragment>
            <div className="cell small-12 medium-6 large-3">
                <span>Search</span>
                <SearchBar
                    onDidChange={(newValue) => searchTextDidChange(newValue)}
                />
            </div>

            <div className="cell small-12 medium-6 large-3">
                <span>Organisation</span>
                <ReactSelect
                    name="organisationID"
                    className="react-select"
                    onChange={(e) => organisationDidChange(e.target.value)}
                    value={searchOrganisationID}
                    options={organisationOptions}
                />
            </div>
        </React.Fragment>

    )

    const table = (
        <ReactTable
            columns={columns}
            data={items}
            page={page}
            pages={totalPages}
            pageSize={pageSize}
            loading={isLoading}
            multisort={true}
            className='-striped'
            manual
            onFetchData={(state) => {
                setPage(state.page);
                setPageSize(state.pageSize);
                setSorted(state.sorted);
            }}
        />
    )

    const buttons = [];

    if (PermissionUtil.create(PermissionUtil.PermissionCategories.conversationTypes)) {
        buttons.push(
            <button
                type="button"
                className="button small"
                onClick={handleAddNewItem}
            >
                New Conversation
            </button>
        )
    }

    return (
        <React.Fragment>
            {showCopyButton &&
                <React.Fragment>
                    <div className="grid-x grid-padding-x">
                        <div className="small-3 cell">
                            <label htmlFor="useTimeLimit" className="text-right middle">Use time limit</label>
                        </div>
                        <div className="large-4 small-9 cell">
                            <label className="chkbx-container">
                                <input className="chkbx" type="checkbox" name="useTimeLimit" onChange={(e) => setUseTimeLimit(e.target.checked)} checked={useTimeLimit} />
                                <span className="chkmk"></span>
                            </label>
                        </div>
                    </div>
                    {useTimeLimit &&
                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="timeLimit" className="text-right middle">Time limit in minutes</label>
                            </div>
                            <div className="large-4 small-9 cell">
                                <input type="number" min={1} value={timeLimit} onChange={(e) => setTimeLimit(e.target.value)} />
                            </div>
                        </div>
                    }
                    <div className="grid-x grid-padding-x">
                        <div className="small-3 cell">
                            <label htmlFor="useSameSession" className="text-right middle">Use same session</label>
                        </div>
                        <div className="large-4 small-9 cell">
                            <label className="chkbx-container">
                                <input className="chkbx" type="checkbox" name="useSameSession" onChange={(e) => setUseSameSession(e.target.checked)} checked={useSameSession} />
                                <span className="chkmk"></span>
                            </label>
                        </div>
                    </div>
                </React.Fragment>
            }

            <TableContainer
                title=""
                filters={filters}
                buttons={buttons}
                table={table}
            />

            <Modal
                classNames={{modal:"modal-medium"}}
                open={modalOpen}
                modalId="Modal"
                onClose={closeModal}
                center
            >
                {modalContent}
            </Modal>

        </React.Fragment>
    );

}
